import React, { useEffect, useRef, useState, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * NavDropdown — accessible disclosure menu for a top-level nav item with children.
 * - Trigger is a <button> with aria-expanded / aria-controls.
 * - Opens on hover (pointer) and on click / Enter / Space / ArrowDown (keyboard).
 * - Escape closes and returns focus to the trigger; outside click closes.
 * - Arrow keys move focus between items; Home/End jump.
 * - Active state reflects the current route.
 */
export const NavDropdown = ({ item, light, onImage = false }) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const itemRefs = useRef([]);
  const closeTimer = useRef(null);
  const hoverOpened = useRef(false);
  const { pathname } = useLocation();
  const menuId = `nav-menu-${item.id}`;

  const isActive = item.children.some((c) => c.href === pathname);

  const clearTimer = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };
  const openNow = (viaHover = false) => {
    clearTimer();
    hoverOpened.current = viaHover;
    setOpen(true);
  };
  const closeSoon = () => {
    clearTimer();
    closeTimer.current = setTimeout(() => setOpen(false), 140);
  };

  const close = useCallback((refocus = false) => {
    clearTimer();
    hoverOpened.current = false;
    setOpen(false);
    if (refocus) triggerRef.current?.focus();
  }, []);

  // Click: open if closed; if it was opened by hover, a click keeps it open
  // (so mouse users don't accidentally dismiss it); otherwise toggle closed.
  const onTriggerClick = () => {
    if (!open) return openNow(false);
    if (hoverOpened.current) {
      hoverOpened.current = false;
      return undefined;
    }
    return close();
  };

  // Close on outside click / route change.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) close();
    };
    document.addEventListener("pointerdown", onDown);
    return () => document.removeEventListener("pointerdown", onDown);
  }, [open, close]);

  useEffect(() => { setOpen(false); }, [pathname]);

  useEffect(() => () => clearTimer(), []);

  const focusItem = (i) => {
    const n = item.children.length;
    const idx = ((i % n) + n) % n;
    itemRefs.current[idx]?.focus();
  };

  const onTriggerKeyDown = (e) => {
    if (e.key === "ArrowDown" || e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      setOpen(true);
      requestAnimationFrame(() => focusItem(0));
    } else if (e.key === "Escape") {
      close(true);
    }
  };

  const onMenuKeyDown = (e) => {
    const current = itemRefs.current.indexOf(document.activeElement);
    switch (e.key) {
      case "ArrowDown": e.preventDefault(); focusItem(current + 1); break;
      case "ArrowUp": e.preventDefault(); focusItem(current - 1); break;
      case "Home": e.preventDefault(); focusItem(0); break;
      case "End": e.preventDefault(); focusItem(item.children.length - 1); break;
      case "Escape": e.preventDefault(); close(true); break;
      case "Tab": close(); break;
      default: break;
    }
  };

  return (
    <div
      ref={rootRef}
      className="relative"
      onPointerEnter={(e) => { if (e.pointerType !== "touch") openNow(true); }}
      onPointerLeave={(e) => { if (e.pointerType !== "touch") closeSoon(); }}
    >
      {/* Label navigates to the section landing page; the arrow toggles the dropdown. */}
      <div
        className={cn(
          "relative inline-flex min-h-11 items-center",
          light ? "text-[rgba(255,253,248,0.92)]" : "text-[var(--wf-ink)]",
          light && onImage && "[text-shadow:0_1px_10px_rgba(0,0,0,0.35)]",
          isActive && !light && "text-[var(--wf-maroon)]",
          isActive && light && "text-white"
        )}
      >
        {item.labelOnly ? (
          <button
            type="button"
            id={`${menuId}-label`}
            data-testid={`${item.testid}-link`}
            onClick={onTriggerClick}
            onKeyDown={onTriggerKeyDown}
            aria-expanded={open}
            aria-controls={menuId}
            aria-haspopup="true"
            className={cn(
              "wf-underline inline-flex min-h-11 items-center rounded-sm text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ring-offset-background",
              light ? "hover:text-white" : "hover:text-[var(--wf-maroon)]"
            )}
          >
            {item.label}
          </button>
        ) : (
          <Link
            to={item.href}
            id={`${menuId}-label`}
            aria-current={pathname === item.href ? "page" : undefined}
            data-testid={`${item.testid}-link`}
            onClick={() => close()}
            className={cn(
              "wf-underline inline-flex min-h-11 items-center rounded-sm text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ring-offset-background",
              light ? "hover:text-white" : "hover:text-[var(--wf-maroon)]"
            )}
          >
            {item.label}
          </Link>
        )}
        <button
          ref={triggerRef}
          type="button"
          id={`${menuId}-trigger`}
          aria-expanded={open}
          aria-controls={menuId}
          aria-haspopup="true"
          aria-label={`${open ? "Close" : "Open"} ${item.label} menu`}
          data-testid={`${item.testid}-trigger`}
          onClick={onTriggerClick}
          onKeyDown={onTriggerKeyDown}
          className={cn(
            "ml-0.5 inline-flex h-8 w-7 items-center justify-center rounded-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ring-offset-background",
            light ? "hover:text-white" : "hover:text-[var(--wf-maroon)]"
          )}
        >
          <ChevronDown
            aria-hidden="true"
            className={cn("h-4 w-4 transition-transform duration-200", open && "rotate-180")}
          />
        </button>
        {isActive && (
          <span
            aria-hidden="true"
            data-testid={`${item.testid}-active-indicator`}
            className={cn("absolute inset-x-0 bottom-1.5 h-0.5", light ? "bg-white/90" : "bg-[var(--wf-maroon)]")}
          />
        )}
      </div>

      {/* Panel */}
      <div
        id={menuId}
        role="menu"
        aria-labelledby={`${menuId}-label`}
        data-testid={`${item.testid}-menu`}
        onKeyDown={onMenuKeyDown}
        className={cn(
          "absolute left-1/2 top-full z-[100] w-[16rem] -translate-x-1/2 pt-3 transition-[opacity,transform] duration-200",
          open
            ? "pointer-events-auto translate-y-0 opacity-100"
            : "pointer-events-none -translate-y-1 opacity-0"
        )}
        aria-hidden={!open}
      >
        <div className="overflow-hidden rounded-sm border border-[var(--wf-border)] bg-[rgba(255,253,248,0.96)] p-2 shadow-[var(--wf-shadow)] backdrop-blur-xl">
          <span aria-hidden="true" className="absolute left-1/2 top-1.5 h-3 w-3 -translate-x-1/2 rotate-45 border-l border-t border-[var(--wf-border)] bg-[rgba(255,253,248,0.96)]" />
          <ul className="relative flex flex-col">
            {item.children.map((c, i) => {
              const active = c.href === pathname;
              return (
                <li key={c.id} role="none">
                  <Link
                    to={c.href}
                    role="menuitem"
                    tabIndex={open ? 0 : -1}
                    aria-current={active ? "page" : undefined}
                    ref={(el) => { itemRefs.current[i] = el; }}
                    data-testid={`nav-${item.id}-${c.id}-link`}
                    onClick={() => close()}
                    className={cn(
                      "group flex items-center rounded-sm px-3.5 py-2.5 transition-colors duration-150 hover:bg-[var(--wf-cream)] focus-visible:outline-none focus-visible:bg-[var(--wf-cream)] focus-visible:ring-2 focus-visible:ring-ring",
                      active && "bg-[var(--wf-wheat)]/70"
                    )}
                  >
                    <span className={cn("text-sm font-semibold text-[var(--wf-ink)] group-hover:text-[var(--wf-maroon)]", active && "text-[var(--wf-maroon)]")}>
                      {c.label}
                    </span>
                  </Link>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </div>
  );
};

/* Mobile disclosure (accordion) for a nav item with children.
 * Collapsed by default; the label navigates to the parent page, the arrow
 * button expands/collapses the sub-menu. */
export const MobileNavGroup = ({ item, renderLink, renderParentLink }) => {
  const [open, setOpen] = useState(false);
  const id = `mobile-nav-${item.id}`;
  return (
    <li className="border-b border-[var(--wf-border)]">
      <div className="flex items-stretch justify-between gap-2">
        {renderParentLink(item)}
        <button
          type="button"
          aria-expanded={open}
          aria-controls={id}
          aria-label={`${open ? "Collapse" : "Expand"} ${item.label} menu`}
          data-testid={`mobile-${item.testid}-toggle`}
          onClick={() => setOpen((v) => !v)}
          className="inline-flex min-h-12 w-12 shrink-0 items-center justify-center rounded-md text-[var(--wf-ink)] hover:bg-[var(--wf-wheat)]/60 hover:text-[var(--wf-maroon)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <ChevronDown aria-hidden="true" className={cn("h-5 w-5 transition-transform duration-200", open && "rotate-180")} />
        </button>
      </div>
      <ul id={id} className={cn("mb-2 flex-col pl-3", open ? "flex" : "hidden")}>
        {item.children.map((c) => (
          <li key={c.id}>{renderLink(c, item)}</li>
        ))}
      </ul>
    </li>
  );
};
